class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Track if we're currently loading to prevent double-loading
   * @type {boolean}
   */
  #isLoading = false;

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Log attribute changes for debugging
      console.log('Product recommendations attribute changed:', mutation.attributeName, mutation.oldValue, '->', mutation.attributeName ? this.getAttribute(mutation.attributeName) : 'null');

      // Ignore class changes (like adding 'hidden')
      if (mutation.attributeName === 'class') {
        continue;
      }

      // Ignore data-error changes
      if (mutation.attributeName === 'data-error') {
        continue;
      }

      // Handle data-recommendations-performed changes
      // When changed externally (by morph/hydration), it signals we should reload
      if (mutation.attributeName === 'data-recommendations-performed') {
        const oldValue = mutation.oldValue;
        const newValue = this.dataset.recommendationsPerformed;
        
        // If it changed from true to false externally, reload recommendations
        if (oldValue === 'true' && newValue === 'false' && !this.#isLoading) {
          console.log('Recommendations performed flag reset by morph/hydration - reloading');
          this.#loadRecommendations();
        }
        continue;
      }

      // When data-product-id changes, reload recommendations
      if (mutation.attributeName === 'data-product-id') {
        const oldValue = mutation.oldValue;
        const newValue = this.dataset.productId;
        
        // Only reload if the product ID actually changed to a different value
        if (oldValue !== newValue && newValue && !this.#isLoading) {
          console.log('Product ID changed from', oldValue, 'to', newValue, '- reloading recommendations');
          this.#loadRecommendations();
        }
        break;
      }

      // Only reload for specific attribute changes that matter
      if (mutation.attributeName && ['data-section-id', 'data-intent', 'data-url'].includes(mutation.attributeName)) {
        if (!this.#isLoading) {
          this.#loadRecommendations();
        }
        break;
      }
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { 
      attributes: true,
      attributeOldValue: true 
    });
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    // Prevent double-loading
    if (this.#isLoading) {
      console.log('Already loading recommendations, skipping duplicate request');
      return;
    }

    this.#isLoading = true;

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      })
      .finally(() => {
        this.#isLoading = false;
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    // Debug logging
    console.log('Product recommendations fetch URL:', url);
    console.log('Product ID:', productId);
    console.log('Section ID:', sectionId);
    console.log('Intent:', intent);

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      console.log('Using cached response for:', url);
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      console.log('Fetching recommendations from:', url);
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        console.error('Fetch failed with status:', response.status);
        return { success: false, status: response.status };
      }

      const text = await response.text();
      console.log('Response received, length:', text.length);
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    // Abort errors are expected when we cancel outdated fetches, so don't treat them as real errors
    if (error.name === 'AbortError') {
      console.log('Product recommendations fetch aborted (expected when product changes)');
      return;
    }

    console.error('Product recommendations error:', error);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
